import type {
    AxisID,
    ChartAnimationPhase,
    DynamicContext,
    NormalisedBaseAxisLabelOptions,
    NormalisedBaseCartesianAxisOptions,
    ResolvedTextAlign,
    Scale,
    ZoomMinMax,
} from 'ag-charts-core';
import {
    ChartAxisDirection,
    StateMachine,
    arraysEqual,
    calcLineHeight,
    countLines,
    diffArrays,
    findMinMax,
    isPlainObject,
    resolveTextAlign,
} from 'ag-charts-core';
import type {
    AgAxisTitleOrientation,
    AgCartesianAxisPosition,
    AgTimeInterval,
    AgTimeIntervalUnit,
    TextAlign,
    VerticalAlign,
} from 'ag-charts-types';

import type { AxisContext } from '../../module/axisContext';
import type { ChartRegistry } from '../../module/moduleContext';
import { type FromToDiff, fromToMotion } from '../../motion/fromToMotion';
import { resetMotion } from '../../motion/resetMotion';
import { ContinuousScale } from '../../scale/continuousScale';
import { DiscreteTimeScale } from '../../scale/discreteTimeScale';
import { BBox } from '../../scene/bbox';
import { TranslatableGroup } from '../../scene/group';
import { type Node, PointerEvents } from '../../scene/node';
import { Selection } from '../../scene/selection';
import { Line } from '../../scene/shape/line';
import { Rect } from '../../scene/shape/rect';
import { TransformableText } from '../../scene/shape/text';
import { Transformable } from '../../scene/transformable';
import type { AxisPrimaryTickCount } from '../../util/secondaryAxisTicks';
import { Caption } from '../caption';
import type { ChartLayout } from '../chartAxis';
import type { AnimationManager } from '../interaction/animationManager';
import { expandLabelPadding } from '../label';
import type { ScrollbarLayout } from '../layout/layoutManager';
import { Axis, AxisGroupZIndexMap, type LabelNodeDatum } from './axis';
import {
    type LabelBox,
    type LabelExtent,
    getAxisLabelSideFlag,
    getBandEdgeOffset,
    getRotatedLabelExtent,
    getTextAlignShift,
    getTickLabelEdgeOffsets,
    getVerticalAlignShift,
    getVerticalBandEdgeOffset,
} from './axisLabelUtil';
import type {
    AxisFillDatum,
    AxisGroupDatumTranslation,
    AxisLabelDatum,
    AxisLineDatum,
    AxisLineDatumCoords,
    TickDatum,
} from './axisUtil';
import {
    NiceMode,
    prepareAxisAnimationContext,
    prepareAxisAnimationFunctions,
    resetAxisFillSelectionFn,
    resetAxisGroupFn,
    resetAxisLabelSelectionFn,
    resetAxisLineSelectionFn,
} from './axisUtil';
import { generateTicks } from './generateTicks';

type AxisAnimationState = 'empty' | 'ready';
type AxisAnimationEvent = { reset: undefined; resize: undefined; update: FromToDiff };

interface GeneratedTicks {
    ticks: TickDatum[];
    tickLines: AxisLineDatum[];
    gridLines: AxisLineDatum[];
    gridFills: AxisFillDatum[];
    labels: LabelNodeDatum[];
    spacing: number;
}

export type GridLineStyleTickDatum = Pick<TickDatum, 'index' | 'tickId' | 'translation'>;

interface TitleOrientationLayout {
    rotation: number;
    textAlign: CanvasTextAlign;
    textBaseline: CanvasTextBaseline;
}

/** The orientation that reproduces the default title rendering for an axis position. */
function defaultTitleOrientation(position: AgCartesianAxisPosition): AgAxisTitleOrientation {
    if (position === 'left') return 'vertical';
    if (position === 'right') return 'vertical-reversed';
    return 'horizontal';
}

/** True when the title text runs across the axis line rather than along it. */
function isTitleAcrossAxis(position: AgCartesianAxisPosition, orientation: AgAxisTitleOrientation): boolean {
    const axisVertical = position === 'left' || position === 'right';
    return (orientation === 'horizontal') === axisVertical;
}

const OPPOSITE_BASELINE: Record<VerticalAlign, VerticalAlign> = { top: 'bottom', middle: 'middle', bottom: 'top' };

const titleRotations: Record<AgAxisTitleOrientation, number> = {
    horizontal: 0,
    vertical: -Math.PI / 2,
    'vertical-reversed': Math.PI / 2,
};

/**
 * Maps an axis title `orientation` to the rotation, alignment and baseline that place the title on
 * the outer side of the axis line at its midpoint. The rotation is screen-relative; the alignment
 * and baseline keep the title clear of the axis line for the side it sits on.
 */
function getTitleOrientationLayout(
    position: AgCartesianAxisPosition,
    orientation: AgAxisTitleOrientation
): TitleOrientationLayout {
    const rotation = titleRotations[orientation];

    if (isTitleAcrossAxis(position, orientation)) {
        let textAlign: CanvasTextAlign;
        switch (position) {
            case 'left':
                textAlign = 'right';
                break;
            case 'right':
                textAlign = 'left';
                break;
            case 'top':
                textAlign = orientation === 'vertical-reversed' ? 'right' : 'left';
                break;
            case 'bottom':
                textAlign = orientation === 'vertical-reversed' ? 'left' : 'right';
                break;
        }
        return { rotation, textAlign, textBaseline: 'middle' };
    }

    let textBaseline: CanvasTextBaseline;
    switch (position) {
        case 'top':
            textBaseline = 'bottom';
            break;
        case 'bottom':
            textBaseline = 'top';
            break;
        case 'left':
            textBaseline = orientation === 'vertical' ? 'bottom' : 'top';
            break;
        case 'right':
            textBaseline = orientation === 'vertical-reversed' ? 'bottom' : 'top';
            break;
    }
    return { rotation, textAlign: 'center', textBaseline };
}

export abstract class CartesianAxis<
    S extends Scale<D, number, any> = Scale<any, number, any>,
    D = any,
    TOptions extends NormalisedBaseCartesianAxisOptions = NormalisedBaseCartesianAxisOptions,
> extends Axis<S, D, GeneratedTicks, TOptions> {
    static is(value: unknown): value is CartesianAxis<any> {
        return value instanceof CartesianAxis;
    }

    get position(): AgCartesianAxisPosition {
        return this.options.position!;
    }

    readonly crossAxisTranslation: { x: number; y: number } = { x: 0, y: 0 };

    private titleThickness = 0;

    minimumTimeGranularity: AgTimeIntervalUnit | undefined = undefined;

    // Used to define the range the axis line will occupy, e.g. when bandAlignment is not 'justify'.
    lineRange?: [number, number];

    // Axis-local zoom. Managed by ZoomManager via setZoom(). Defaults to the full range; diverges
    // from the direction primary only in independent-axes mode.
    private zoom: ZoomMinMax = { min: 0, max: 1 };

    public getZoom(): ZoomMinMax {
        return this.zoom;
    }

    public setZoom(zoom: ZoomMinMax): void {
        this.zoom = { min: zoom.min, max: zoom.max };
    }

    protected animationManager: AnimationManager;

    protected readonly headingLabelGroup = this.axisGroup.appendChild(
        new TranslatableGroup<AxisGroupDatumTranslation>({ name: `${this.id}-Axis-heading` })
    );
    protected readonly lineNodeGroup = this.axisGroup.appendChild(
        new TranslatableGroup<AxisGroupDatumTranslation>({ name: `${this.id}-Axis-line` })
    );
    protected readonly lineNode = this.lineNodeGroup.appendChild(
        new Line<AxisLineDatumCoords>({ zIndex: AxisGroupZIndexMap.AxisLine })
    );

    protected tickLineGroupSelection = Selection.select<Line<AxisLineDatum>>(this.tickLineGroup, Line, false);
    protected gridLineGroupSelection = Selection.select<Line<AxisLineDatum>>(this.gridLineGroup, Line, false);
    protected gridFillGroupSelection = Selection.select<Rect<AxisFillDatum>>(this.gridFillGroup, Rect, false);

    private readonly tempText = new TransformableText({ debugDirty: false });
    private readonly tempCaption = new Caption();

    protected readonly animationState: StateMachine<AxisAnimationState, AxisAnimationEvent>;

    protected get horizontal() {
        return this.position === 'top' || this.position === 'bottom';
    }

    constructor(moduleCtx: DynamicContext<ChartRegistry>, id: AxisID, scale: S, options: TOptions) {
        super(moduleCtx, id, scale, options);

        this.animationManager = moduleCtx.animationManager;

        this.animationState = new StateMachine<AxisAnimationState, AxisAnimationEvent>('empty', {
            empty: {
                update: {
                    target: 'ready',
                    action: () => this.resetSelectionNodes(),
                },
                reset: 'empty',
            },
            ready: {
                update: (data: FromToDiff) => this.animateReadyUpdate(data),
                resize: () => this.resetSelectionNodes(),
                reset: 'empty',
            },
        });

        this.headingLabelGroup.appendChild(this.caption.node);

        let previousSize: readonly [number, number] | undefined = undefined;
        this.cleanup.register(
            moduleCtx.eventsHub.on('layout:complete', (e) => {
                // Fire resize animation action if chart canvas size changes.
                const size = [e.chart.width, e.chart.height] as const;
                if (previousSize != null && !arraysEqual(size, previousSize)) {
                    this.animationState.transition('resize');
                }
                previousSize = size;
            }),
            this.caption.registerInteraction(this.moduleCtx, this.id)
        );
    }

    /**
     * Canvas-space alignment for a configured `label.textAlign`. Nullish means the axis's own
     * computed alignment, which is not the resolver's concern, so it passes straight through.
     */
    private resolveLabelTextAlign(textAlign: TextAlign | undefined): ResolvedTextAlign | undefined {
        return textAlign == null ? undefined : resolveTextAlign(textAlign, this.moduleCtx.domManager.isRtl);
    }

    protected override onGridVisibilityChange(): void {
        // Do nothing, the grid lines and fills are updated in the update method.
    }

    override resetAnimation(phase: ChartAnimationPhase): void {
        if (phase === 'initial') {
            this.animationState.transition('reset');
        }
    }

    get direction() {
        return this.position === 'top' || this.position === 'bottom' ? ChartAxisDirection.X : ChartAxisDirection.Y;
    }

    protected override get userListeners() {
        return this.options.listeners;
    }

    override createAxisContext(): AxisContext {
        // Mutate rather than spread so the live getters defined on the base context
        // (`range`, `gridLength`, `mirrored`, etc.) are preserved.
        const ctx = super.createAxisContext();
        ctx.position = this.position;
        return ctx;
    }

    protected updateDirection() {
        switch (this.position) {
            case 'top':
                this.mirrored = true;
                this.parallel = true;
                break;
            case 'right':
                this.mirrored = true;
                this.parallel = false;
                break;
            case 'bottom':
                this.mirrored = false;
                this.parallel = true;
                break;
            case 'left':
                this.mirrored = false;
                this.parallel = false;
                break;
        }

        if (this.axisContext) {
            this.axisContext.position = this.position;
            this.axisContext.direction = this.direction;
        }
    }

    override calculateLayout(primaryTickCount?: AxisPrimaryTickCount, chartLayout?: ChartLayout) {
        this.updateDirection();
        return super.calculateLayout(primaryTickCount, chartLayout);
    }

    override calculateTickLayout(
        domain: D[],
        niceMode: NiceMode[],
        visibleRange: [number, number],
        initialPrimaryTickCount?: AxisPrimaryTickCount
    ): {
        niceDomain: D[];
        tickDomain: D[];
        ticks: D[];
        rawTickCount: number | undefined;
        fractionDigits: number;
        timeInterval: AgTimeInterval | AgTimeIntervalUnit | undefined;
        bbox: BBox;
        layout: GeneratedTicks;
    } {
        const sideFlag = getAxisLabelSideFlag(this.mirrored);
        const label = this.options.label;
        const labelX = sideFlag * (this.getTickSize() + this.getTickSpacing() + label.spacing + this.seriesAreaPadding);
        const scrollbar = this.chartLayout?.scrollbars?.[this.id];
        const scrollbarThickness = this.getScrollbarThickness(scrollbar);

        if (
            niceMode[0] === NiceMode.Off &&
            niceMode[1] === NiceMode.Off &&
            !label.enabled &&
            !(this.primaryLabel?.enabled ?? false) &&
            this.options.tick.enabled === false &&
            !(this.primaryTick?.enabled ?? false) &&
            this.options.gridLine.enabled === false &&
            !this.isPickComputationEnabled()
        ) {
            const { bbox, spacing } = this.measureAxisLayout(domain, [], [], scrollbar, scrollbarThickness);
            // Performance optimization: if ticks have no effect, don't generate them
            this.setPickTickData([]);
            const layout: GeneratedTicks = {
                ticks: [],
                tickLines: [],
                gridLines: [],
                gridFills: [],
                labels: [],
                spacing,
            };
            return {
                ticks: [],
                rawTickCount: 0,
                tickDomain: domain,
                niceDomain: domain,
                fractionDigits: 0,
                timeInterval: undefined,
                bbox,
                layout,
            };
        }

        const { primaryLabel, scale, range, defaultTickMinSpacing, minimumTimeGranularity } = this;
        const { interval, reverse } = this.options;

        const tickGenerationResult = generateTicks({
            label,
            parallel: this.parallel,
            scale,
            interval,
            primaryLabel,
            domain,
            range,
            reverse,
            niceMode,
            visibleRange,
            defaultTickMinSpacing,
            minimumTimeGranularity,
            sideFlag,
            labelOffset: labelX,
            primaryTickCount: initialPrimaryTickCount,
            axisRotation: this.horizontal ? Math.PI / -2 : 0,
            isVertical: this.direction === ChartAxisDirection.Y,
            sizeLimit: this.chartLayout?.sizeLimit,
            inRange: (translation: number) => this.inRange(translation, 0.001),
            labelBaseline: this.resolveLabelBaseline(label.verticalAlign),
            labelBandOffsets: this.bandFlushesLabels()
                ? (ticks, rotation, textAlign, textBaseline) =>
                      this.measureLabelBandOffsets(ticks, { rotation, textAlign, textBaseline }, scrollbarThickness)
                : undefined,
            tickFormatter: (...args) => this.tickFormatter(...args),
        });

        const { tickData } = tickGenerationResult;
        const removeOverflowLabels =
            (label?.avoidCollisions ?? true) &&
            this.horizontal &&
            tickData.ticks.length > 2 &&
            (ContinuousScale.is(this.scale) || DiscreteTimeScale.is(this.scale));

        if (removeOverflowLabels) {
            const labelTextAlign = this.resolveLabelTextAlign(label?.textAlign);
            // Selecting the alignment the axis already computes must leave the labels untouched, so
            // the edge arithmetic only departs from its centre-anchored form once the two differ.
            const alignmentOverride = labelTextAlign === tickGenerationResult.textAlign ? undefined : labelTextAlign;
            // A banded scale anchors an aligned label on the band edge rather than the tick, so the
            // edges the overflow test measures move with it.
            const bandEdgeOffset = getBandEdgeOffset(this.scale.bandwidth ?? 0, labelTextAlign);
            const labelEdges = (width: number) => {
                const { leading, trailing } = getTickLabelEdgeOffsets(
                    width,
                    tickGenerationResult.rotation,
                    alignmentOverride
                );
                return { leading: leading + bandEdgeOffset, trailing: trailing + bandEdgeOffset };
            };
            const removeOverflowThreshold = this.chartLayout?.padding.right ?? 0;
            const lastTick = tickData.ticks.at(-1);
            const fullVisibleRange = visibleRange[0] === 0 && visibleRange[1] === 1;
            if (
                lastTick?.tickLabel != null &&
                lastTick.translation + labelEdges(lastTick.textMetrics.width).trailing >
                    range[1] + removeOverflowThreshold
            ) {
                lastTick.tickLabel = undefined;
                if (fullVisibleRange) {
                    tickData.ticks[0].tickLabel = undefined;
                }
            }

            // The leading edge can only overflow once the override pushes the label past the start
            // of the range, so the centre-anchored case has nothing to check.
            const firstTick = tickData.ticks[0];
            if (
                alignmentOverride != null &&
                firstTick?.tickLabel != null &&
                firstTick.translation + labelEdges(firstTick.textMetrics.width).leading <
                    range[0] - (this.chartLayout?.padding.left ?? 0)
            ) {
                firstTick.tickLabel = undefined;
                // Both endpoints go together over the full range, mirroring the trailing branch, so
                // an outward alignment cannot leave one end labelled and the other bare.
                if (fullVisibleRange && lastTick != null) {
                    lastTick.tickLabel = undefined;
                }
            }
        }

        const {
            ticks,
            tickDomain,
            rawTicks,
            rawTickCount,
            fractionDigits,
            timeInterval,
            niceDomain,
            rawFirstTickIndex,
        } = tickData;

        this.setPickTickData(ticks, rawFirstTickIndex);

        const labels = ticks.map((d) => this.getTickLabelProps(d, tickGenerationResult, scrollbarThickness));

        this.alignLabelBands(ticks, labels, tickGenerationResult);

        const { position, gridPadding, gridLength } = this;
        const direction = position === 'bottom' || position === 'right' ? -1 : 1;
        const p1 = direction * gridPadding;
        const p2 = direction * (gridLength + gridPadding);

        const gridLines = this.calculateGridLines(ticks, p1, p2);
        const gridFills = this.calculateGridFills(ticks, p1, p2);
        const tickLines = this.calculateTickLines(ticks, direction, scrollbarThickness);
        const { bbox, spacing } = this.measureAxisLayout(tickDomain, ticks, labels, scrollbar, scrollbarThickness);
        const layout: GeneratedTicks = { ticks, gridLines, gridFills, tickLines, labels, spacing };

        return { ticks: rawTicks, rawTickCount, tickDomain, niceDomain, fractionDigits, timeInterval, bbox, layout };
    }

    protected calculateGridLines(ticks: GridLineStyleTickDatum[], p1: number, p2: number) {
        return ticks.map((tick, index) => this.calculateGridLine(tick, index, p1, p2, ticks));
    }

    protected calculateGridLine(
        { index: tickIndex, tickId, translation: offset }: GridLineStyleTickDatum,
        _index: number,
        p1: number,
        p2: number,
        _ticks: GridLineStyleTickDatum[]
    ): AxisLineDatum {
        const { horizontal } = this;
        const gridLine = this.options.gridLine;

        const [x1, y1, x2, y2] = horizontal ? [offset, p1, offset, p2] : [p1, offset, p2, offset];
        const { style } = gridLine;
        const { stroke, strokeWidth = 0, lineDash } = style[tickIndex % style.length] ?? {};

        return { tickId, offset, x1, y1, x2, y2, stroke, strokeWidth, lineDash };
    }

    protected calculateGridFills(ticks: GridLineStyleTickDatum[], p1: number, p2: number) {
        const { horizontal, range, type } = this;

        const gridFills: AxisFillDatum[] = [];
        if (ticks.length == 0) return gridFills;

        let gridFillIndexOffset = 0;
        const isVerticalUnitTime = !horizontal && type === 'unit-time';
        const firstFillOffCanvas =
            (isVerticalUnitTime && ticks[0].translation < range[0]) ||
            (!isVerticalUnitTime && ticks[0].translation > range[0]);

        if (firstFillOffCanvas) {
            const injectedTick = { tickId: `before:${ticks[0].tickId}`, translation: range[0] };
            gridFills.push(this.calculateGridFill(injectedTick, -1, ticks[0].index, p1, p2, ticks));
            gridFillIndexOffset = 1;
        }

        gridFills.push(
            ...ticks.map((tick, index) =>
                this.calculateGridFill(tick, index, tick.index + gridFillIndexOffset, p1, p2, ticks)
            )
        );

        return gridFills;
    }

    protected calculateGridFill(
        { tickId, translation }: Pick<GridLineStyleTickDatum, 'tickId' | 'translation'>,
        index: number,
        gridFillIndex: number,
        p1: number,
        p2: number,
        ticks: GridLineStyleTickDatum[]
    ): AxisFillDatum {
        const { horizontal, range } = this;
        const gridLine = this.options.gridLine;

        const nextTick = ticks[index + 1];
        const startOffset = translation;
        const endOffset = nextTick ? nextTick.translation : range[1];

        const [x1, y1, x2, y2] = horizontal
            ? [startOffset, Math.max(p1, p2), endOffset, Math.min(p1, p2)]
            : [Math.min(p1, p2), Math.min(startOffset, endOffset), Math.max(p1, p2), Math.max(startOffset, endOffset)];
        const { fill, fillOpacity } = gridLine.style[gridFillIndex % gridLine.style.length] ?? {};

        return { tickId, x1, y1, x2, y2, fill, fillOpacity };
    }

    protected calculateTickLines(
        ticks: TickDatum[],
        direction: number,
        scrollbarThickness: number = 0
    ): AxisLineDatum[] {
        return ticks.map((tick) => this.calculateTickLine(tick, tick.index, direction, ticks, scrollbarThickness));
    }

    protected calculateTickLine(
        { isPrimary, tickId, translation: offset }: Pick<TickDatum, 'isPrimary' | 'tickId' | 'translation'>,
        _index: number,
        direction: number,
        _ticks: TickDatum[],
        scrollbarThickness: number = 0
    ): AxisLineDatum {
        const { horizontal, primaryTick } = this;
        const tick = this.options.tick;

        const datumTick = isPrimary && primaryTick ? primaryTick : tick;
        const tickSize = this.getTickSize(datumTick);
        const tickSpacing = this.getTickSpacing(datumTick);
        const tickOffset = -direction * (scrollbarThickness + tickSpacing);
        const h = -direction * tickSize;
        const [x1, y1, x2, y2] = horizontal
            ? [offset, tickOffset, offset, tickOffset + h]
            : [tickOffset, offset, tickOffset + h, offset];
        const { stroke, width: strokeWidth } = datumTick;
        const lineDash = undefined;

        return { tickId, offset, x1, y1, x2, y2, stroke, strokeWidth, lineDash };
    }

    override update() {
        this.updateDirection();

        const previousTicksIds = Array.from(this.tickLabelGroupSelection.nodes(), (node) => node.unsafeDatum.tickId);

        super.update();

        const { tickLayout } = this;
        this.updateTitle(this.scale.domain, this.resolveTitleSpacing(tickLayout?.spacing ?? 0));

        if (!this.animatable) {
            this.moduleCtx.animationManager.skipCurrentBatch();
        }

        if (tickLayout) {
            const { ticks } = tickLayout;

            if (this.animationManager.isSkipped()) {
                this.resetSelectionNodes();
            } else {
                const tickIds = ticks.map((datum) => datum.tickId);
                const diff = diffArrays(previousTicksIds, tickIds);
                this.animationState.transition('update', diff);
            }
        }

        const { enabled, stroke, width } = this.options.line;
        // Without this the layout isn't consistent when enabling/disabling the line, padding configurations are not respected.
        this.lineNode.setProperties({ stroke, strokeWidth: enabled ? width : 0 });

        this.updateTickLines();
        this.updateGridLines();
        this.updateGridFills();
    }

    private getAxisTransform() {
        return {
            completeTransform: {
                translationX: Math.floor(this.translation.x + this.crossAxisTranslation.x),
                translationY: Math.floor(this.translation.y + this.crossAxisTranslation.y),
            },
            positionOnlyTransform: {
                translationX: Math.floor(this.translation.x),
                translationY: Math.floor(this.translation.y),
            },
        };
    }

    protected override getLayoutTranslation(): { x: number; y: number } {
        const {
            completeTransform: { translationX, translationY },
        } = this.getAxisTransform();
        return { x: translationX, y: translationY };
    }

    override getLayoutState() {
        const layout = super.getLayoutState();
        const crosshairOffset = this.isCrosshairLabelAtEdge() ? { x: 0, y: 0 } : { ...this.crossAxisTranslation };
        return { ...layout, position: this.position, crosshairOffset };
    }

    protected override updatePosition(): void {
        super.updatePosition();

        const { completeTransform, positionOnlyTransform } = this.getAxisTransform();
        const labelsTransform = this.isLabelsAtEdge() ? positionOnlyTransform : completeTransform;
        this.tickLineGroup.datum = labelsTransform;
        this.tickLabelGroup.datum = labelsTransform;
        this.lineNodeGroup.datum = completeTransform;
        this.headingLabelGroup.datum = this.isTitleAtEdge() ? positionOnlyTransform : completeTransform;
    }

    protected override getCanvasBounds(): BBox {
        const labelsAtEdge = this.isLabelsAtEdge();

        if (!labelsAtEdge && !this.isTitleAtEdge()) return super.getCanvasBounds();

        // Interaction follows the ticks and labels, so a group drawn at the other location is left out
        // instead of merged in — everything between the two belongs to the series.
        const groups: Node[] = [this.tickLineGroup, this.tickLabelGroup];
        if (!labelsAtEdge) groups.push(this.lineNodeGroup);
        if (this.titleRendersWithLabels()) groups.push(this.headingLabelGroup);

        return BBox.merge(groups.map((group) => Transformable.toCanvas(group)));
    }

    setAxisVisible(visible: boolean) {
        this.tickLineGroup.visible = visible && (this.options.tick.enabled || (this.primaryTick?.enabled ?? false));
        this.tickLabelGroup.visible = visible && (this.options.label.enabled || (this.primaryTick?.enabled ?? false));
        this.lineNodeGroup.visible = visible;
        this.headingLabelGroup.visible = visible;
    }

    private getAxisLineCoordinates() {
        const { horizontal } = this;
        const [c1, c2] = findMinMax(this.lineRange ?? this.range);

        return horizontal ? { x1: c1, x2: c2, y1: 0, y2: 0 } : { x1: 0, x2: 0, y1: c1, y2: c2 };
    }

    private getTickLineBBox(datum: TickDatum, scrollbarThickness: number) {
        const { translation } = datum;
        const { position, primaryTick } = this;
        let tickSize = this.getTickSize();
        if (primaryTick?.enabled) {
            tickSize = Math.max(tickSize, this.getTickSize(primaryTick));
        }
        const direction = position === 'bottom' || position === 'right' ? -1 : 1;
        const tickSpacing = this.getTickSpacing(this.options.tick);
        const tickOffset = -direction * (scrollbarThickness + tickSpacing);
        const start = tickOffset;
        const end = tickOffset - direction * (tickSize + tickSpacing);
        const min = Math.min(start, end);
        const max = Math.max(start, end);
        switch (position) {
            case 'top':
                return new BBox(translation, min, 0, max - min);
            case 'bottom':
                return new BBox(translation, min, 0, max - min);
            case 'left':
                return new BBox(min, translation, max - min, 0);
            case 'right':
                return new BBox(min, translation, max - min, 0);
        }
    }

    protected lineNodeBBox() {
        const { position, seriesAreaPadding } = this;
        const { y1, y2 } = this.getAxisLineCoordinates();
        const dy = y2 - y1;
        switch (position) {
            case 'top':
                return new BBox(y1, -seriesAreaPadding, dy, seriesAreaPadding);
            case 'bottom':
                return new BBox(y1, 0, dy, seriesAreaPadding);
            case 'left':
                return new BBox(-seriesAreaPadding, y1, seriesAreaPadding, dy);
            case 'right':
                return new BBox(0, y1, seriesAreaPadding, dy);
        }
    }

    protected titleBBox(domain: D[], spacing: number) {
        const { tempCaption } = this;
        tempCaption.node.setProperties(this.titleProps(tempCaption, domain, spacing));
        this.wrapTitleText(tempCaption);
        return tempCaption.node.getBBox();
    }

    private wrapTitleText(caption: Caption) {
        const axisLength = Math.abs(this.range[1] - this.range[0]) || Infinity;
        const thickness = this.options.thickness ?? Infinity;
        const orientation = this.options.title.orientation ?? defaultTitleOrientation(this.position);
        if (isTitleAcrossAxis(this.position, orientation)) {
            caption.computeTextWrap(thickness, axisLength);
        } else {
            caption.computeTextWrap(axisLength, thickness);
        }
    }

    protected getScrollbarThickness(scrollbar?: ScrollbarLayout): number {
        if (!scrollbar?.enabled) return 0;
        return scrollbar.placement === 'inner' ? scrollbar.spacing + scrollbar.thickness : 0;
    }

    protected resolveScrollbarLayout(
        scrollbar: ScrollbarLayout | undefined,
        labelThickness: number
    ): (ScrollbarLayout & { offset: number }) | undefined {
        if (!scrollbar) return;

        const { position } = this;
        const direction = position === 'top' || position === 'left' ? -1 : 1;

        if (scrollbar.placement === 'inner') {
            const offset = direction === 1 ? scrollbar.spacing : -scrollbar.spacing - scrollbar.thickness;
            return { ...scrollbar, offset };
        }

        const offset =
            direction === 1
                ? labelThickness + scrollbar.spacing
                : -labelThickness - scrollbar.spacing - scrollbar.thickness;

        return { ...scrollbar, offset };
    }

    protected applyScrollbarLayout(
        boxes: BBox[],
        labelThickness: number,
        scrollbar: ScrollbarLayout | undefined
    ): { spacing: number; scrollbarLayout: (ScrollbarLayout & { offset: number }) | undefined } {
        const scrollbarLayout = this.resolveScrollbarLayout(scrollbar, labelThickness);

        let spacing = labelThickness;
        if (scrollbarLayout) {
            const { offset, thickness, placement } = scrollbarLayout;
            if (placement === 'outer') {
                spacing += scrollbarLayout.spacing + thickness;
            }
            if (this.horizontal) {
                boxes.push(new BBox(0, offset, 0, thickness));
            } else {
                boxes.push(new BBox(offset, 0, thickness, 0));
            }
        }

        return { spacing, scrollbarLayout };
    }

    private measureAxisLayout(
        domain: D[],
        ticks: TickDatum[],
        labels: LabelNodeDatum[],
        scrollbar: ScrollbarLayout | undefined,
        scrollbarThickness: number
    ) {
        const { primaryTick, primaryLabel, position, horizontal, seriesAreaPadding } = this;
        const tick = this.options.tick;
        const label = this.options.label;
        const title = this.options.title;
        const boxes: BBox[] = [];

        boxes.push(this.lineNodeBBox());

        if (tick.enabled || primaryTick?.enabled) {
            for (const datum of ticks) {
                boxes.push(this.getTickLineBBox(datum, scrollbarThickness));
            }
        }

        const { tempText } = this;
        if (label.enabled) {
            for (const datum of labels) {
                if (!datum.visible) continue;

                tempText.setProperties(datum);

                const box = tempText.getBBox();
                if (box) {
                    boxes.push(box);
                }
            }
        }

        if (primaryLabel?.enabled && position === 'bottom') {
            const inexactMeasurementPadding = 2;

            // Force base min-height
            boxes.push(
                new BBox(
                    0,
                    calcLineHeight(label.fontSize) + inexactMeasurementPadding,
                    1,
                    this.getTickSize(tick) + this.getTickSpacing(tick) + label.spacing + seriesAreaPadding
                )
            );

            if (primaryLabel.format != null) {
                const { format } = primaryLabel;
                const formats = isPlainObject(format) ? Object.values(format) : [format];
                const maxLines = formats.reduce((m, f) => Math.max(m, countLines(f)), 0);
                boxes.push(
                    new BBox(
                        0,
                        this.getTickSize(primaryTick ?? tick) +
                            this.getTickSpacing(primaryTick ?? tick) +
                            primaryLabel.spacing +
                            seriesAreaPadding,
                        1,
                        maxLines * calcLineHeight(primaryLabel.fontSize) + inexactMeasurementPadding
                    )
                );
            }
        }

        const combined = BBox.merge(boxes);
        const labelThickness = horizontal ? combined.height : combined.width;
        const { spacing, scrollbarLayout } = this.applyScrollbarLayout(boxes, labelThickness, scrollbar);
        this.layout.labelThickness = labelThickness;
        this.layout.scrollbar = scrollbarLayout;

        this.titleThickness = 0;
        if (title.enabled) {
            const titleBox = this.titleBBox(domain, this.resolveTitleSpacing(spacing));
            boxes.push(titleBox);

            const titleExtent = BBox.merge([this.lineNodeBBox(), titleBox]);
            this.titleThickness = horizontal ? titleExtent.height : titleExtent.width;
        }

        const bbox = BBox.merge(boxes);
        return { bbox, spacing };
    }

    private isTitleAtEdge() {
        return this.options.crossAt?.titlePlacement === 'edge';
    }

    private isLabelsAtEdge() {
        return this.options.crossAt?.labelPlacement === 'edge';
    }

    private isCrosshairLabelAtEdge() {
        return (this.options.crossAt?.crosshairLabelPlacement ?? 'edge') === 'edge';
    }

    crosshairLabelNeedsEdgeSpace(): boolean {
        if (!this.isCrosshairLabelAtEdge()) return false;
        return this.getModuleMap().getModule<{ labelEnabled: boolean }>('crosshair')?.labelEnabled === true;
    }

    /** The title and labels render in the same place unless exactly one of them is at the edge. */
    private titleRendersWithLabels() {
        return this.isTitleAtEdge() === this.isLabelsAtEdge();
    }

    /** The title only has to clear the labels when both render in the same place. */
    private resolveTitleSpacing(spacing: number) {
        return this.titleRendersWithLabels() ? spacing : 0;
    }

    /** Thickness occupied at the `position` edge and at the crossing point, which `crossAt` can split apart. */
    getCrossAtThicknesses(): { atEdge: number; atCrossing: number } {
        const titleAtEdge = this.isTitleAtEdge();
        const labelsAtEdge = this.isLabelsAtEdge();
        const crosshairLabelAtEdge = this.crosshairLabelNeedsEdgeSpace();

        const title = this.titleThickness;
        const labels = this.layout.labelThickness ?? 0;

        return {
            atEdge: Math.max(titleAtEdge ? title : 0, labelsAtEdge || crosshairLabelAtEdge ? labels : 0),
            atCrossing: Math.max(titleAtEdge ? 0 : title, labelsAtEdge ? 0 : labels),
        };
    }

    protected titleProps(caption: Caption, domain: D[], spacing: number) {
        const title = this.options.title;

        if (!title.enabled) {
            caption.enabled = false;
            return {
                visible: false,
                text: '',
                textAlign: 'center' as const,
                textBaseline: 'bottom' as const,
                x: 0,
                y: 0,
                rotationCenterX: 0,
                rotationCenterY: 0,
                rotation: 0,
            };
        }

        caption.enabled = true;
        caption.color = title.color;
        caption.fontFamily = title.fontFamily;
        caption.fontSize = title.fontSize;
        caption.fontStyle = title.fontStyle;
        caption.fontWeight = title.fontWeight;
        caption.wrapping = title.wrapping;
        caption.truncate = title.truncate;
        caption.maxWidth = title.maxWidth;
        caption.maxHeight = title.maxHeight;

        const { range } = this;
        const midOffset = (range[0] + range[1]) / 2;
        const padding = title.spacing + spacing;

        let x: number;
        let y: number;
        switch (this.position) {
            case 'top':
                x = midOffset;
                y = -padding;
                break;
            case 'bottom':
                x = midOffset;
                y = padding;
                break;
            case 'left':
                x = -padding;
                y = midOffset;
                break;
            case 'right':
                x = padding;
                y = midOffset;
                break;
        }

        const { rotation, textAlign, textBaseline } = getTitleOrientationLayout(
            this.position,
            title.orientation ?? defaultTitleOrientation(this.position)
        );

        const { formatter = (p) => p.defaultValue } = title;
        const text = this.cachedCallWithContext(formatter, this.getTitleFormatterParams(domain));
        caption.text = text;

        return {
            visible: true,
            text,
            textAlign,
            textBaseline,
            x,
            y,
            rotationCenterX: x,
            rotationCenterY: y,
            rotation,
        };
    }

    /** See {@link AxisLayout.label.boxOffset}, which this is reported through. */
    private labelBoxOffset(label: NormalisedBaseAxisLabelOptions): number {
        return -getAxisLabelSideFlag(this.mirrored) * expandLabelPadding(label)[this.position];
    }

    protected override getLabelBoxOffset(): number {
        return this.labelBoxOffset(this.options.label);
    }

    private getTickLabelProps(
        datum: TickDatum,
        tickGenerationResult: { rotation: number; textAlign: ResolvedTextAlign; textBaseline: VerticalAlign },
        scrollbarThickness: number
    ): LabelNodeDatum {
        const { horizontal, primaryLabel, primaryTick, seriesAreaPadding, scale } = this;
        const { tickId, tickLabel: text = '', translation, isPrimary, textUntruncated } = datum;
        const label = isPrimary && primaryLabel?.enabled ? primaryLabel : this.options.label;
        const tick = isPrimary && primaryTick?.enabled ? primaryTick : this.options.tick;
        const { rotation } = tickGenerationResult;
        // A configured `label.textAlign` overrides the alignment derived from the axis side and the
        // label rotation. Unset (the default) leaves the computed value untouched.
        const labelTextAlign = this.resolveLabelTextAlign(label.textAlign);
        const textAlign = labelTextAlign ?? tickGenerationResult.textAlign;
        const textBaseline = this.resolveLabelBaseline(label.verticalAlign) ?? tickGenerationResult.textBaseline;
        const { range } = scale;
        const sideFlag = getAxisLabelSideFlag(this.mirrored);
        let labelOffset =
            sideFlag *
            (this.getTickSize(tick) +
                this.getTickSpacing(tick) +
                label.spacing +
                seriesAreaPadding +
                this.labelBoxOffset(label));

        if (scrollbarThickness) {
            labelOffset += sideFlag * scrollbarThickness;
        }
        const visible = text !== '';

        // A band scale ticks the middle of each band, but a configured alignment aligns against the
        // band edge; only the horizontal axis needs the correction (`alignLabelBands` covers vertical).
        const bandEdgeOffset = horizontal ? getBandEdgeOffset(scale.bandwidth ?? 0, labelTextAlign) : 0;
        // The transpose: `verticalAlign` acts along a vertical axis, so it is that axis that needs
        // the band-edge correction (`alignLabelBands` covers the horizontal one).
        const bandEdgeOffsetY = horizontal ? 0 : getVerticalBandEdgeOffset(scale.bandwidth ?? 0, label.verticalAlign);

        const x = horizontal ? translation + bandEdgeOffset : labelOffset;
        const y = horizontal ? -labelOffset : translation + bandEdgeOffsetY;

        return {
            ...this.getLabelStyles({ value: datum.tick, formattedValue: text }, undefined, label),
            tickId,
            rotation,
            text,
            textAlign,
            textBaseline,
            textUntruncated,
            visible,
            x,
            y,
            rotationCenterX: x,
            rotationCenterY: y,
            range,
        };
    }

    // A continuous vertical axis has no band to align within, so `'top'` means above the tick.
    private resolveLabelBaseline(verticalAlign: VerticalAlign | undefined): VerticalAlign | undefined {
        if (verticalAlign == null || this.horizontal || this.scale.bandwidth != null) return verticalAlign;
        return OPPOSITE_BASELINE[verticalAlign];
    }

    /**
     * Whether a configured `label.verticalAlign` will band-flush this axis's labels across the axis
     * line. Only a horizontal axis flushes on `verticalAlign`; on a vertical one the option acts
     * along the axis, where it cannot change the separation collision avoidance measures.
     */
    private bandFlushesLabels() {
        return (
            this.horizontal && (this.options.label.verticalAlign != null || this.primaryLabel?.verticalAlign != null)
        );
    }

    /**
     * How far the band flush will move each label across the axis, run on a throwaway copy of the
     * label data so collision avoidance can measure the geometry it will actually render. Shares
     * `alignLabelBands` rather than restating its band maths, so the two cannot drift apart.
     */
    private measureLabelBandOffsets(
        ticks: TickDatum[],
        tickGenerationResult: { rotation: number; textAlign: ResolvedTextAlign; textBaseline: VerticalAlign },
        scrollbarThickness: number
    ): number[] {
        const labels = ticks.map((d) => this.getTickLabelProps(d, tickGenerationResult, scrollbarThickness));
        const before = labels.map((l) => l.y);
        this.alignLabelBands(ticks, labels, tickGenerationResult);
        return labels.map((l, i) => l.y - before[i]);
    }

    /**
     * Re-anchor labels so that a configured `label.textAlign` / `label.verticalAlign` aligns them
     * within the band the axis reserved, rather than around their own anchor point - the latter
     * grows the glyphs inwards across the axis line and over the series area, where the series
     * paints over them.
     *
     * Each option owns the axis's cross direction for the orientation it acts across: `textAlign` on
     * a vertical axis and `verticalAlign` on a horizontal one. In the other orientation the option
     * acts along the axis instead, so only rotation can carry the glyphs across the axis line; the
     * correction there restores the cross-axis placement the axis's own computed alignment would
     * have produced, leaving the along-axis alignment alone.
     */
    private alignLabelBands(
        ticks: TickDatum[],
        labels: LabelNodeDatum[],
        tickGenerationResult: { rotation: number; textAlign: ResolvedTextAlign; textBaseline: VerticalAlign }
    ) {
        const leafLabel = this.options.label;
        const { primaryLabel } = this;

        if (
            leafLabel.textAlign == null &&
            primaryLabel?.textAlign == null &&
            leafLabel.verticalAlign == null &&
            primaryLabel?.verticalAlign == null
        ) {
            return;
        }

        const { rotation, textAlign: computedTextAlign, textBaseline: computedTextBaseline } = tickGenerationResult;
        const { horizontal, tempText } = this;
        const sideFlag = getAxisLabelSideFlag(this.mirrored);
        const primaryEnabled = primaryLabel?.enabled ?? false;
        // Only a rotated label can swing its glyphs across the axis line without the band flush
        // below; an unrotated one is already placed by its own alignment.
        const anchorRotated = rotation !== 0;

        // Each label tier occupies its own band, so a tier that leaves both options unset keeps
        // today's anchor.
        for (const primaryTier of [false, true]) {
            const tierLabel = primaryTier ? primaryLabel : leafLabel;
            const textAlign = this.resolveLabelTextAlign(tierLabel?.textAlign);
            const verticalAlign = tierLabel?.verticalAlign;

            // Whether this tier needs geometry at all, decided before anything is measured: this
            // runs twice per layout per axis, so a tier the anchor already places correctly must not
            // pay for a measurement pass it cannot use.
            const alignsAcross = horizontal ? verticalAlign != null : textAlign != null;
            // A band flush is a stronger guarantee than restoring the computed cross-axis offset, so
            // where both options are set the flush owns the direction and suppresses the correction.
            const correctsRotation =
                anchorRotated && !alignsAcross && (horizontal ? textAlign != null : verticalAlign != null);
            if (!alignsAcross && !correctsRotation) continue;

            // `getBBox()` includes the box padding where the label is boxed, but the alignments
            // anchor the text, so the extents are taken from the glyph box.
            const padding = expandLabelPadding(tierLabel);

            const measured: { datum: LabelNodeDatum; glyphBox: LabelBox; extent: LabelExtent }[] = [];
            let maxLabelExtent = 0;
            // The edge of the reserved band nearest the axis line, taken across the whole tier - see
            // the band it is turned into below.
            let bandInner: number | undefined;
            for (let i = 0; i < labels.length; i += 1) {
                if ((ticks[i].isPrimary && primaryEnabled) !== primaryTier) continue;

                const datum = labels[i];
                if (!datum.visible) continue;

                tempText.setProperties(datum);
                // The transformed box already carries the label's rotation, which the extents below
                // apply themselves.
                const box = tempText.computeBBoxWithoutTransforms();
                if (box == null) continue;

                const glyphBox = {
                    x: box.x + padding.left,
                    y: box.y + padding.top,
                    width: box.width - padding.left - padding.right,
                    height: box.height - padding.top - padding.bottom,
                };
                const extent = getRotatedLabelExtent(glyphBox, datum.x, datum.y, rotation);
                if (horizontal && verticalAlign != null) {
                    // Where the axis's own computed alignment would have put these glyphs, which is
                    // the space the axis actually reserved. A rotated label swings on both
                    // alignments, so both are restored: taking the baseline alone would derive the
                    // band from a label still carrying a configured `textAlign`, and place it inside
                    // the series area.
                    const computedExtent = getRotatedLabelExtent(
                        {
                            ...glyphBox,
                            x:
                                textAlign == null
                                    ? glyphBox.x
                                    : glyphBox.x + getTextAlignShift(glyphBox.width, textAlign, computedTextAlign),
                            y: glyphBox.y + getVerticalAlignShift(glyphBox.height, verticalAlign, computedTextBaseline),
                        },
                        datum.x,
                        datum.y,
                        rotation
                    );
                    // `getTickLabelProps` negates the label offset on `y`, so outward of the axis
                    // line is `-sideFlag` here - the opposite sign to the vertical axis's `x` below.
                    const labelInner = datum.y + (sideFlag === -1 ? computedExtent.y0 : computedExtent.y1);
                    bandInner ??= labelInner;
                    // One band per tier, not one per label: labels of differing size must flush to a
                    // common edge, so the origin is the natural placement reaching nearest the axis
                    // line - the same edge the axis sized its reservation from.
                    bandInner = sideFlag === -1 ? Math.min(bandInner, labelInner) : Math.max(bandInner, labelInner);
                }
                measured.push({ datum, glyphBox, extent });
                // The band runs across the axis, so its depth is the deepest label in that direction.
                const labelExtent = horizontal ? extent.y1 - extent.y0 : extent.x1 - extent.x0;
                maxLabelExtent = Math.max(maxLabelExtent, labelExtent);
            }

            if (maxLabelExtent <= 0) continue;

            // The tier's shared band: `maxLabelExtent` deep, outward of the natural inner edge.
            let band: { start: number; end: number } | undefined;
            if (bandInner != null) {
                const bandOuter = bandInner - sideFlag * maxLabelExtent;
                band = { start: Math.min(bandInner, bandOuter), end: Math.max(bandInner, bandOuter) };
            }

            for (const { datum, glyphBox, extent } of measured) {
                if (correctsRotation) {
                    if (horizontal && textAlign != null) {
                        datum.y += this.getCrossAxisCorrection(
                            datum,
                            glyphBox,
                            extent,
                            rotation,
                            textAlign,
                            computedTextAlign
                        );
                        datum.rotationCenterY = datum.y;
                    } else if (!horizontal && verticalAlign != null) {
                        datum.x += this.getVerticalCrossAxisCorrection(
                            datum,
                            glyphBox,
                            extent,
                            rotation,
                            this.resolveLabelBaseline(verticalAlign)!,
                            computedTextBaseline
                        );
                        datum.rotationCenterX = datum.x;
                    }
                    continue;
                }

                if (band != null) {
                    let y;
                    if (verticalAlign === 'top') {
                        y = band.start - extent.y0;
                    } else if (verticalAlign === 'bottom') {
                        y = band.end - extent.y1;
                    } else {
                        y = (band.start + band.end) / 2 - (extent.y0 + extent.y1) / 2;
                    }

                    datum.y = y;
                    datum.rotationCenterY = y;
                    continue;
                }

                // `inner` is the band edge nearest the axis line - taken verbatim so the tick,
                // padding and scrollbar terms are inherited rather than re-derived.
                const inner = datum.x;
                const outer = inner + sideFlag * maxLabelExtent;
                const bandStart = Math.min(inner, outer);
                const bandEnd = Math.max(inner, outer);

                let x;
                if (textAlign === 'left') {
                    x = bandStart - extent.x0;
                } else if (textAlign === 'right') {
                    x = bandEnd - extent.x1;
                } else {
                    x = (bandStart + bandEnd) / 2 - (extent.x0 + extent.x1) / 2;
                }

                datum.x = x;
                datum.rotationCenterX = x;
            }
        }
    }

    /**
     * Shift that moves a rotated label back to the cross-axis offset the axis's computed alignment
     * would have given it, so the reserved band still contains it.
     */
    private getCrossAxisCorrection(
        datum: LabelNodeDatum,
        glyphBox: LabelBox,
        extent: LabelExtent,
        rotation: number,
        textAlign: ResolvedTextAlign,
        computedTextAlign: ResolvedTextAlign
    ) {
        const shift = getTextAlignShift(glyphBox.width, textAlign, computedTextAlign);
        const computedExtent = getRotatedLabelExtent(
            { ...glyphBox, x: glyphBox.x + shift },
            datum.x,
            datum.y,
            rotation
        );
        // The band lies outward of the axis line, so the edge to pin is the one facing it.
        return getAxisLabelSideFlag(this.mirrored) === -1
            ? computedExtent.y0 - extent.y0
            : computedExtent.y1 - extent.y1;
    }

    /**
     * Shift that moves a rotated label back to the cross-axis offset the axis's computed baseline
     * would have given it, so the reserved column still contains it. The transpose of
     * `getCrossAxisCorrection`, for a vertical axis where `verticalAlign` acts along the axis.
     */
    private getVerticalCrossAxisCorrection(
        datum: LabelNodeDatum,
        glyphBox: LabelBox,
        extent: LabelExtent,
        rotation: number,
        textBaseline: VerticalAlign,
        computedTextBaseline: VerticalAlign
    ) {
        const shift = getVerticalAlignShift(glyphBox.height, textBaseline, computedTextBaseline);
        const computedExtent = getRotatedLabelExtent(
            { ...glyphBox, y: glyphBox.y + shift },
            datum.x,
            datum.y,
            rotation
        );
        // The column lies outward of the axis line, so the edge to pin is the one facing it.
        return getAxisLabelSideFlag(this.mirrored) === -1
            ? computedExtent.x0 - extent.x0
            : computedExtent.x1 - extent.x1;
    }

    protected updateSelections() {
        if (!this.tickLayout) return;

        const lineData = this.getAxisLineCoordinates();
        const { tickLines, gridLines, gridFills, labels } = this.tickLayout;

        const getDatumId = (datum: AxisLabelDatum | AxisLineDatum | AxisFillDatum) => datum.tickId;

        this.lineNode.datum = lineData;
        this.gridLineGroupSelection.update(this.options.gridLine.enabled ? gridLines : [], undefined, getDatumId);
        this.gridFillGroupSelection.update(this.options.gridLine.enabled ? gridFills : [], undefined, getDatumId);
        this.tickLineGroupSelection.update(tickLines, undefined, getDatumId);
        const labelsEnabled = this.options.label.enabled || (this.primaryLabel?.enabled ?? false);
        this.tickLabelGroupSelection.update(labelsEnabled ? labels : [], undefined, getDatumId);
    }

    protected updateGridLines() {
        this.gridLineGroupSelection.each((line, datum) => {
            line.stroke = datum.stroke;
            line.strokeWidth = datum.strokeWidth;
            line.lineDash = datum.lineDash;
        });
    }

    protected updateGridFills() {
        this.gridFillGroupSelection.each((rect, datum) => {
            rect.fill = datum.fill;
            rect.fillOpacity = datum.fillOpacity ?? 1;
        });
    }

    protected updateTickLines() {
        this.tickLineGroupSelection.each((line, datum) => {
            line.stroke = datum.stroke;
            line.strokeWidth = datum.strokeWidth;
            line.lineDash = datum.lineDash;
        });
    }

    protected updateTitle(domain: D[], spacing: number): void {
        const { caption } = this;
        const titleProps = this.titleProps(caption, domain, spacing);
        caption.node.visible = titleProps.visible;
        caption.node.text = titleProps.text;
        caption.node.textAlign = titleProps.textAlign;
        caption.node.textBaseline = titleProps.textBaseline;
        caption.node.datum = titleProps;

        if (titleProps.visible) {
            this.wrapTitleText(caption);
        }
    }

    protected updateLabels() {
        const labelsEnabled = this.options.label.enabled || (this.primaryLabel?.enabled ?? false);
        if (!labelsEnabled) return;

        // Apply label option values
        this.tickLabelGroupSelection.each((node, datum) => {
            node.fill = datum.color;
            node.text = datum.text;
            node.textBaseline = datum.textBaseline;
            node.textAlign = datum.textAlign ?? 'center';
            node.pointerEvents = datum.textUntruncated == null ? PointerEvents.None : PointerEvents.All;
            node.setFont(datum);
            node.setBoxing(datum);
        });
    }

    private animateReadyUpdate(diff: FromToDiff) {
        const { animationManager } = this.moduleCtx;
        const selectionCtx = prepareAxisAnimationContext(this);
        const fns = prepareAxisAnimationFunctions(selectionCtx);

        fromToMotion(
            this.id,
            'axis-group',
            animationManager,
            [this.lineNodeGroup, this.tickLabelGroup, this.tickLineGroup, this.headingLabelGroup],
            fns.group
        );
        fromToMotion(this.id, 'line', animationManager, [this.lineNode], fns.line);
        fromToMotion(
            this.id,
            'line-paths',
            animationManager,
            [this.gridLineGroupSelection, this.tickLineGroupSelection],
            fns.tick,
            (_, d) => d.tickId,
            diff
        );
        fromToMotion(
            this.id,
            'tick-labels',
            animationManager,
            [this.tickLabelGroupSelection],
            fns.label,
            (node) => node.unsafeDatum.tickId,
            diff
        );
        fromToMotion(
            this.id,
            'title',
            animationManager,
            [this.caption.node],
            fns.label,
            (node) => node.unsafeDatum.tickId,
            diff
        );
    }

    protected resetSelectionNodes() {
        resetMotion(
            [this.lineNodeGroup, this.tickLabelGroup, this.tickLineGroup, this.headingLabelGroup],
            resetAxisGroupFn()
        );
        resetMotion([this.gridLineGroupSelection, this.tickLineGroupSelection], resetAxisLineSelectionFn());
        resetMotion([this.gridFillGroupSelection], resetAxisFillSelectionFn());
        resetMotion([this.tickLabelGroupSelection], resetAxisLabelSelectionFn());
        resetMotion([this.caption.node], resetAxisLabelSelectionFn());
        resetMotion([this.lineNode], resetAxisLineSelectionFn());
    }
}
